import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Table } from "primeng/table";
import { ConfirmationDialogService } from "src/app/shared/services/confirmation-dialog.service";
import { EvaluationOverviewService } from "src/app/shared/services/evaluation-overview.service";
import { DateTime } from 'luxon';
import { saveAs } from 'file-saver';
import { Paginator } from "primeng/paginator";
import { StorageService } from "src/app/shared/services/storage.service";
import { TabTitleService } from "src/app/shared/services/tab-title.service";
import { EvaluationJudgingService } from "src/app/shared/services/evaluation-judging.service";
import { getFileNameFromResponse } from "../../../shared/utils/http.util";
import { GridService } from "src/app/shared/services/grid.service";
import { GridConfigModalService } from "src/app/shared/services/grid-config-modal.service";
import { GridNames, IGridConfig } from "src/app/shared/model/grid-config.model";
import { createFromGridColumns } from "src/app/shared/model/user-form-data-filter-model";
import { CopyToClipboardService } from "src/app/shared/services/copy-to-clipboard.service";

@Component({
  selector: "app-evaluation-overview-details",
  templateUrl: "./evaluation-overview-details.component.html",
  styleUrls: ["./evaluation-overview-details.component.scss"],
})
export class EvaluationOverviewDetailsComponent implements OnInit {

  @ViewChild('pTable') table: Table;
  data = [];
  columns = [];
  columnsForUseInExportOnly = [];

  judgeId = '';
  judgeName = '';
  deadline = '';
  sortApplied = false;
  filters: any = {};

  loading = false;

  gridConfig: IGridConfig = {
    gridName: '',
    stageId: '',
    stages: {},
    columns: [],
    updatedAt: '',
    updatedBy: ''    
  };

  // reassign

  @ViewChild('availableJudgesTable') availableJudgesTable: Table;
  @ViewChild('availableJudgesPaginator') availableJudgesPaginator: Paginator;
  @ViewChild('searchInputAvailableJudges') searchInputAvailableJudges: ElementRef;
  selectedJudgment = null;
  selectedJudge = null;
  judgementId = '';
  stageId = '';
  reassignDialog = false;
  availableJudges = [];
  availableJudgesFirst = 0;
  availableJudgesLast = 0;
  availableJudgesPageCount = 0;
  totalAvailableJudges = 0;
  availableJudgesRowsPerPage = 10;
  availableJudgesFilters: any = {};
  availableJudgesColumns = [
    { field: 'fullName', header: 'To Judge' },
    { field: 'superJudgeStatus', header: 'Super Judge' },
    { field: 'assignedReviews', header: '# Assigned' },
    { field: 'completedReviews', header: '# Completed' },
    { field: 'openReviews', header: 'Open Submissions' },
    { field: 'lastLogin', header: 'Last Login' },
  ]
  sortAppliedAvailableJudges = false;
  filterAppliedAvailableJudges = false;

  // used to enable/disable focus trap for reassignment modal 
  dynamicFocusTrap = true;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private evaluationOverviewService: EvaluationOverviewService,
    private judgmentService: EvaluationJudgingService,
    private activatedRoute: ActivatedRoute,
    private storageService: StorageService,
    private router: Router,
    private tabTitleService: TabTitleService,
    private gridService: GridService,
    private gridConfigModalService: GridConfigModalService,
    private copyToClipboard: CopyToClipboardService,
  ) {

  }

  async ngOnInit() {
    this.activatedRoute.params.subscribe(async params => {
      this.judgeId = params.judge;
      this.stageId = params.id;
      await this.getColumns();
      await this.getData();
    });
    this.availableJudgesRowsPerPage = this.storageService.retrieve('availableJudgesRowsPerPage') ?? 10;
    Object.assign(this.availableJudgesFilters, { PageSize: this.availableJudgesRowsPerPage });
  }

  async refresh() {
    await this.getData();
  }

  async getData() {
    this.loading = true;
    if (!this.filters.userFormDataFilters) {
      Object.assign(this.filters, { userFormDataFilters: createFromGridColumns(this.columns) });
    }
    const res: any = await this.evaluationOverviewService.GetEvaluationOverviewDetailsList(this.judgeId, this.filters).toPromise();
    this.judgeName = res.judgeName;
    this.deadline = res.deadline;
    this.tabTitleService.setTabTitle(`${res.stageName} Overview`);
    this.data = res.data?.map(judgement => {
      judgement.actions = judgement.actions?.map(action => {
        return {
          label: action.name,
          disabled: action.value === 1 && this.isDeadlinePassed(),
          command: async () => {
            switch (action.value) {
              case 0:
                //review
                const url = this.router.serializeUrl(this.router.createUrlTree([`evaluation/${this.stageId}/review/${judgement.id}`], { queryParams: { step: 1 } }));
                window.open(url, '_blank');
                break;
              case 1:
                //reasign
                this.judgementId = judgement.id;
                this.selectedJudgment = judgement;
                this.selectedJudge = null;
                this.sortAppliedAvailableJudges = false;
                this.filterAppliedAvailableJudges = false;
                this.availableJudgesFilters = {};
                Object.assign(this.availableJudgesFilters, { PageSize: this.availableJudgesRowsPerPage });
                await this.getAvailableJudges();
                this.reassignDialog = true;
                break;
              case 2:
                // submit
                this.confirmationDialogService.confirm('Confirmation', `Are you sure you want to submit this review?`, 'Confirm', 'Cancel', '600px')
                  .then(async result => {
                    if (result) {
                      const res: any = await this.judgmentService.getJudgementSteps(judgement.id).toPromise();
                      const tatalSteps = res.data.length;
                      await this.judgmentService.saveJudgementSteps({
                        judgementId: judgement.id,
                        step: tatalSteps,
                      }).toPromise();
                      await this.getData();
                    }
                  });
                break;
              case 3:
                //reopen
                this.confirmationDialogService.confirm('Confirmation', `Are you sure you want to reopen this review?`, 'Confirm', 'Cancel', '600px')
                  .then(async result => {
                    if (result) {
                      await this.judgmentService.editReview(judgement.id).toPromise();
                      await this.getData();
                    }
                  });
                break;
              default:
                break;
            }
          }
        }
      })
      judgement.actions.push({
        label: 'View scores',
        command: () => {
          const url = this.router.serializeUrl(this.router.createUrlTree([`evaluation/${this.stageId}/results/${judgement.userId}/${this.judgeId}`]));
          window.open(url, '_blank');
        }
      });
      return judgement;
    });
    this.loading = false;
  }

  exit() {
    this.router.navigate([
      `evaluation/${this.activatedRoute.snapshot.paramMap.get('id')}/overview`,
    ]);
  }

  async onSort(event: any) {
    this.sortApplied = true;
    Object.assign(this.filters, {
      SortField: event.field,
      SortOrder: event.order,
    });
    await this.getData();
  }

  async clearFilters() {
    this.filters = {};
    this.table.clear();
    this.sortApplied = false;
    await this.getData();
  }

  onDownload(event: any) {
    // export filters variable
    let filtersForExport: any = {};
    // assign current filters to export filters
    Object.assign(filtersForExport, this.filters);
    // create filters from all columns that are usable in export and not visible in grid
    // and concat them with the current grid filters
    filtersForExport.userFormDataFilters = this.filters.userFormDataFilters.concat(createFromGridColumns(this.columnsForUseInExportOnly));

    this.evaluationOverviewService
      .ExportEvaluationOverviewDetailsList(this.judgeId, {
        ...filtersForExport,
        ExportFormat: event.code,
      })
      .subscribe((response) => {
        saveAs(response.body, getFileNameFromResponse(response));
      });
  }

  async getAvailableJudges() {
    const res: any = await this.evaluationOverviewService.GetEvaluationAvailableJudges(this.judgementId, this.availableJudgesFilters).toPromise();
    this.availableJudges = res.data;
    const page = (this.availableJudgesFirst / this.availableJudgesRowsPerPage) + 1;
    this.totalAvailableJudges = res?.totalItems;
    this.availableJudgesLast = page === res?.pageCount ? res?.totalItems : this.availableJudgesRowsPerPage * page;
    this.availableJudgesPageCount = res?.pageCount;
  }

  async paginateAvailableJudges(event: any) {
    this.availableJudgesFirst = event.first;
    Object.assign(this.availableJudgesFilters, {
      PageIndex: this.availableJudgesFirst / this.availableJudgesRowsPerPage,
    });
    await this.getAvailableJudges();
  }

  async onSortAvailableJudges(event: any) {
    this.sortAppliedAvailableJudges = true;
    Object.assign(this.availableJudgesFilters, {
      SortField: event.field,
      SortOrder: event.order,
    });
    await this.getAvailableJudges();
  }

  async onFilterAvailableJudges(event: any) {
    this.filterAppliedAvailableJudges = event.filters?.global?.value ? true : false;
    Object.assign(this.availableJudgesFilters, {
      SearchQuery: event.filters.global?.value,
    });
    await this.updateAvailableJudgeTable();
  }

  async onPageAvailableJudges(event: any) {
    if (this.availableJudgesRowsPerPage !== event.rows) {
      this.availableJudgesRowsPerPage = event.rows;
      this.storageService.store('availableJudgesRowsPerPage', this.availableJudgesRowsPerPage);
    }
    Object.assign(this.availableJudgesFilters, {
      PageSize: this.availableJudgesRowsPerPage,
    });
    this.availableJudgesPaginator.changePage(0);
  }

  async updateAvailableJudgeTable() {
    if (this.availableJudgesPaginator.currentPage() === 0) {
      await this.getAvailableJudges();
    } else {
      this.availableJudgesPaginator.changePage(0);
    }
  }

  async clearFiltersAvailableJudges() {
    this.availableJudgesFilters = {};
    this.filterAppliedAvailableJudges = false;
    this.availableJudgesTable.clear();
    this.sortAppliedAvailableJudges = false;
    this.searchInputAvailableJudges.nativeElement.value = '';
    this.selectedJudge = null;
    this.updateAvailableJudgeTable();
  }

  async reassignJudgment() {
    // stop focus trap on reassignment modal
    // so that the confirmation modal has focus trap
    this.dynamicFocusTrap = false;
    this.confirmationDialogService
      .confirm('Confirmation', `Are you sure you want to reassign ${this.selectedJudgment.submittedBy} from ${this.selectedJudgment.judgeName} to ${this.selectedJudge.fullName}?`, 'Confirm', 'Cancel', '600px')
      .then(async (confirmed) => {
        if (confirmed) {
          await this.evaluationOverviewService.reassignJudge(this.selectedJudgment.id, this.selectedJudge.id).toPromise();
          this.reassignDialog = false;
          await this.getData();
        }
        // enable focus trap on the reassignment modal
        this.dynamicFocusTrap = true;
      });
  }

  openAvailableJudgesSearchInput() {
    document
      .getElementById("top-search-aj")
      .classList.add("search-button-open", "table");
    setTimeout(() => this.searchInputAvailableJudges.nativeElement.focus(), 0);
  }

  closeSearchInputAvailableJudges() {
    document
      .getElementById("top-search-aj")
      .classList.remove("search-button-open");
  }

  isDeadlinePassed() {
    const currentDate = DateTime.fromJSDate(new Date()).setZone('UTC').toISO();
    return currentDate > this.deadline;
  }

  async getColumns() {
    const res: any = await this.gridService
      .GetGridConfig(GridNames.EvaluationOverview2, this.stageId)
      .toPromise();
    this.gridConfig = res.data;

    // get all columns that are usable in export and not visible in grid
    this.columnsForUseInExportOnly = JSON.parse(JSON.stringify(
      this.gridConfig.columns.filter((c) => !c.visibleInGrid && c.useInExport)
    ));

    // create a copy of the original grid config object
    // this allows for the grid config modal to be opened with the original settings
    // and not with overridden settings
    this.columns = JSON.parse(JSON.stringify(
      this.gridConfig.columns.filter((c) => c.visibleInGrid)
    ));
  }

  openConfigDialog() {
    this.gridConfigModalService
      .openGridConfigDialog({ 
        gridName: GridNames.EvaluationOverview2, 
        stageId: this.stageId, 
        columns: this.gridConfig.columns,
        updatedAt: this.gridConfig.updatedAt, 
        updatedBy: this.gridConfig.updatedBy
      })
      .then(async res => {
        if (res) {
          await this.getColumns();
          this.clearFilters()
        }
      })
  }

  clickToCopy(stringToCopy: string) {
    this.copyToClipboard.copyToClipboard(stringToCopy);
  }

}
